import * as React from "react";
import { useHistory } from "react-router-dom";
import { useMappedState } from "redux-react-hook";

import Table from "src/core/dashboard/components/widgets/table/Table";
import type { IStore } from "src/core/shared/store";
import { Button } from "src/library/components/Button";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import {
  Invoice,
  useAccountingApi
} from "src/core/accounting/api/accounting.api";
import { invoiceTableColumns } from "src/core/shared/components/tables/invoiceTableColumns";
import { useDrop } from "src/core/drops/hooks";

interface RelatedInvoicesTabProps {
  transactionId?: number;
  orderId?: number
}

const defaultPagination: PaginatedParams<Invoice> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "invoiceDate", "DESC" ] ]
};

/**
 * Related Invoices Tab - displays invoices in a table format
 * Used in: TransactionDetailsPage
 */
const RelatedInvoicesTab: React.FC<RelatedInvoicesTabProps> = ({ transactionId, orderId }) => {
  const history = useHistory();
  const { currentRole } = useMappedState(({ global }: IStore) => ({ currentRole: global.role }));
  const isAdmin = currentRole?.type === "admin";
  const [ { drop } ] = useDrop();
  const [ getInvoicesRes, getInvoices ] = useAccountingApi(drop?.customerId ? "LIST_CUSTOMER_INVOICES" : "LIST_INVOICES");

  // Action button for each row
  const renderActionButton = React.useCallback(
    (info: { row: { original: Invoice } }) => {
      const invoice = info.row.original;
      // Use role-based URL: admin uses /admin/accounting/invoices, customer uses /accounting/invoices
      const invoiceUrl = isAdmin ? `/admin/accounting/invoices/${invoice.id}` : `/accounting/invoices/${invoice.id}`;

      return (
        <Button
          $buttonType="primary"
          onClick={() => history.push(invoiceUrl)}
        >
          View Invoice
        </Button>
      );
    },
    [ history, isAdmin ],
  );

  // Build columns with action button
  // Disable filtering on all columns - this is static data (following RelatedOrdersTab pattern)
  const columns = React.useMemo(() => {
    const baseColumns = invoiceTableColumns({
      showActions: true,
      actionButton: renderActionButton,
      showMatchedValue: true // Show matched value column for transaction-invoice relationship
    });

    return baseColumns;
  }, [ renderActionButton ]);

  // N;-op fetch for static data
  const onFetchData = React.useCallback((params: { filters: FilterParams<Invoice>; pagination: PaginatedParams<Invoice> }) => {
    if (drop?.id) {
      getInvoices({
        customerId: drop.customerId,
        filters: {
          ...params.filters,
          transactionId,
          orderId
        },
        pagination: params.pagination
      });
    } else {
      getInvoices({
        filters: {
          ...params.filters,
          transactionId,
          orderId
        },
        pagination: params.pagination
      });
    }
  }, [
    drop?.id,
    drop?.customerId,
    getInvoices,
    orderId,
    transactionId
  ]);

  return (
    <Table
      tableOf="Related Invoices"
      data={getInvoicesRes.data?.invoices.result ?? []}
      columns={columns}
      loading={getInvoicesRes.pending}
      onFetchData={onFetchData}
      defaultPagination={defaultPagination}
      pageCount={getInvoicesRes.data?.invoices.pages}
      rowCount={getInvoicesRes.data?.invoices.count}
    />
  );
};

export default RelatedInvoicesTab;
